/*  Private Device Vault — namespaced service (iter238).
    Isolates Health and Items into independent IndexedDB namespaces, each with
    its own PBKDF2 salt and AES-GCM key. The same user PIN unlocks both, but
    the derived keys never cross compartments: a Health thumbnail/record is
    cryptographically unreadable with the Items key and vice versa.

    Backward compatible:
      • If a legacy single-DB vault exists on this device (TravelerPulsePrivateVault),
        the first PIN unlock migrates it into the two namespaced DBs transparently.
      • v1 backup files import by migrating into both namespaces.                  */

import { deriveKey, encryptObject, decryptObject, randomSalt, _internals } from "./crypto";
import {
  metaGet, metaSet, metaDelete, vaultGet, vaultPut, vaultDump,
  wipeEverything, requestPersistentStorage, VAULT_NS,
} from "./storage";
import { COMPARTMENT, emptyHealthCompartment, emptyInventoryCompartment } from "./schemas";
import * as bio from "./webauthn";

const META_SALT     = "vault_salt";
const META_SENTINEL = "vault_sentinel";
const META_VERSION  = "vault_schema_version";
const META_HINT     = "vault_pin_hint";
const CURRENT_VERSION = 2;
const AUTOLOCK_MS = 5 * 60 * 1000;

const NS_LIST = [VAULT_NS.HEALTH, VAULT_NS.ITEMS];

const COMPARTMENT_NS = {
  [COMPARTMENT.HEALTH]:    VAULT_NS.HEALTH,
  [COMPARTMENT.INVENTORY]: VAULT_NS.ITEMS,
};

export function nsFor(compartmentId) {
  return COMPARTMENT_NS[compartmentId] || VAULT_NS.DEFAULT;
}

class VaultService {
  constructor() {
    this._keys = {};        // { [ns]: CryptoKey }
    this._keyBytes = {};    // { [ns]: Uint8Array } — kept while unlocked, for biometric wrap
    this._lastActivity = 0;
    this._autoLockTimer = null;
    this._listeners = new Set();
  }

  subscribe(fn) { this._listeners.add(fn); return () => this._listeners.delete(fn); }
  _emit()       { this._listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } }); }

  async isInitialized() {
    for (const ns of NS_LIST) if (await metaGet(META_SALT, ns)) return true;
    if (await metaGet(META_SALT)) return true;         // legacy single-DB vault
    return false;
  }

  isUnlocked() {
    return NS_LIST.every((ns) => !!this._keys[ns]);
  }

  async biometricEnrolled()  { return bio.hasBiometricEnrolled(); }
  async biometricAvailable() { return bio.biometricAvailable(); }

  _touch() {
    this._lastActivity = Date.now();
    if (this._autoLockTimer) clearTimeout(this._autoLockTimer);
    this._autoLockTimer = setTimeout(() => this.lock(), AUTOLOCK_MS);
  }

  async _deriveKeyBytes(pin, salt) {
    const km = await crypto.subtle.importKey(
      "raw", new TextEncoder().encode(pin), { name: "PBKDF2" }, false, ["deriveBits"],
    );
    const bits = await crypto.subtle.deriveBits(
      { name: "PBKDF2", salt, iterations: _internals.PBKDF2_ITERATIONS, hash: "SHA-256" },
      km, 256,
    );
    return new Uint8Array(bits);
  }

  async _keyFromBytes(bytes) {
    return crypto.subtle.importKey(
      "raw", bytes, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"],
    );
  }

  async _seedNamespace(ns, pin, { hint } = {}) {
    const salt = randomSalt();
    const key  = await deriveKey(pin, salt);
    await metaSet(META_SALT, salt, ns);
    await metaSet(META_VERSION, CURRENT_VERSION, ns);
    if (hint) await metaSet(META_HINT, String(hint).slice(0, 80), ns);
    const sentinel = await encryptObject(key, { ok: true, ns, at: new Date().toISOString() });
    await metaSet(META_SENTINEL, sentinel, ns);
    this._keys[ns] = key;
    this._keyBytes[ns] = await this._deriveKeyBytes(pin, salt);
    return { key, salt };
  }

  async bootstrap(pin, options = {}) {
    if (await this.isInitialized()) throw new Error("Vault already initialized on this device");
    if (!pin || pin.length < 4)      throw new Error("PIN must be at least 4 characters");
    for (const ns of NS_LIST) await this._seedNamespace(ns, pin, { hint: options.hint });
    // Seed empty compartments INTO their own namespace
    await vaultPut(COMPARTMENT.HEALTH,
      await encryptObject(this._keys[VAULT_NS.HEALTH], emptyHealthCompartment()), VAULT_NS.HEALTH);
    await vaultPut(COMPARTMENT.INVENTORY,
      await encryptObject(this._keys[VAULT_NS.ITEMS], emptyInventoryCompartment()), VAULT_NS.ITEMS);
    this._touch();
    await requestPersistentStorage();
    this._emit();
    return true;
  }

  async unlockWithPin(pin) {
    const healthSalt  = await metaGet(META_SALT, VAULT_NS.HEALTH);
    const legacySalt  = await metaGet(META_SALT);
    const legacySent  = await metaGet(META_SENTINEL);

    // First launch after upgrade: migrate legacy single-DB vault → two namespaces
    if (!healthSalt && legacySalt && legacySent) {
      return this._migrateLegacyAndUnlock(pin);
    }
    if (!healthSalt) throw new Error("Vault is not set up on this device");

    for (const ns of NS_LIST) {
      const salt = await metaGet(META_SALT, ns);
      const sent = await metaGet(META_SENTINEL, ns);
      if (!salt || !sent) throw new Error(`Vault namespace ${ns} is corrupted — please restore from backup`);
      let key;
      try { key = await deriveKey(pin, salt); }
      catch { throw new Error("Unable to derive key"); }
      try { await decryptObject(key, sent); }
      catch { throw new Error("Incorrect PIN"); }
      this._keys[ns] = key;
      this._keyBytes[ns] = await this._deriveKeyBytes(pin, salt);
    }
    this._touch();
    this._emit();
    return true;
  }

  async _migrateLegacyAndUnlock(pin) {
    const legacySalt = await metaGet(META_SALT);
    const legacySent = await metaGet(META_SENTINEL);
    let legacyKey;
    try { legacyKey = await deriveKey(pin, legacySalt); }
    catch { throw new Error("Unable to derive key"); }
    try { await decryptObject(legacyKey, legacySent); }
    catch { throw new Error("Incorrect PIN"); }

    // Create fresh independent namespaces (new salts) derived from the same PIN
    for (const ns of NS_LIST) await this._seedNamespace(ns, pin);

    // Carry forward the hint if the user set one on the legacy vault
    const hint = await metaGet(META_HINT);
    if (hint) {
      await metaSet(META_HINT, hint, VAULT_NS.HEALTH);
      await metaSet(META_HINT, hint, VAULT_NS.ITEMS);
    }

    // Decrypt each legacy compartment, re-encrypt under its new namespace key
    const legacyRecords = await vaultDump();
    for (const [cid, enc] of Object.entries(legacyRecords || {})) {
      const ns = nsFor(cid);
      if (ns === VAULT_NS.DEFAULT) continue;
      try {
        const obj = await decryptObject(legacyKey, enc);
        const re  = await encryptObject(this._keys[ns], obj);
        await vaultPut(cid, re, ns);
      } catch (e) { console.warn("[vault-migrate] skipped", cid, e?.message); }
    }
    // Keep legacy DB intact as rollback — the next vault.wipe() clears everything.
    this._touch();
    this._emit();
    return true;
  }

  async unlockWithBiometric() {
    const healthSalt = await metaGet(META_SALT, VAULT_NS.HEALTH);
    if (!healthSalt) throw new Error("Vault is not set up on this device");
    const payload = await bio.unlockWithBiometric();
    // payload is now { health: Uint8Array, items: Uint8Array }
    for (const ns of NS_LIST) {
      const bytes = payload?.[ns];
      if (!bytes) throw new Error("Biometric wrap missing — unlock with PIN and re-enroll");
      const key  = await this._keyFromBytes(bytes);
      const sent = await metaGet(META_SENTINEL, ns);
      try { await decryptObject(key, sent); }
      catch { throw new Error("Biometric key mismatch — please unlock with PIN"); }
      this._keys[ns] = key;
      this._keyBytes[ns] = bytes;
    }
    this._touch();
    this._emit();
    return true;
  }

  async enrollBiometric() {
    if (!this.isUnlocked()) throw new Error("Unlock first before enrolling biometric");
    await bio.enrollBiometric("vault-user", {
      [VAULT_NS.HEALTH]: this._keyBytes[VAULT_NS.HEALTH],
      [VAULT_NS.ITEMS]:  this._keyBytes[VAULT_NS.ITEMS],
    });
    this._emit();
    return true;
  }

  async removeBiometric() { await bio.removeBiometric(); this._emit(); }

  lock() {
    for (const ns of NS_LIST) {
      if (this._keyBytes[ns]) this._keyBytes[ns].fill(0);
    }
    this._keys = {};
    this._keyBytes = {};
    if (this._autoLockTimer) { clearTimeout(this._autoLockTimer); this._autoLockTimer = null; }
    this._emit();
  }

  async getHint() {
    return (await metaGet(META_HINT, VAULT_NS.HEALTH)) || (await metaGet(META_HINT));
  }

  async loadCompartment(id) {
    const ns  = nsFor(id);
    const key = this._keys[ns];
    if (!key) throw new Error("Vault is locked");
    this._touch();
    const enc = await vaultGet(id, ns);
    if (!enc) return null;
    return decryptObject(key, enc);
  }

  async saveCompartment(id, obj) {
    const ns  = nsFor(id);
    const key = this._keys[ns];
    if (!key) throw new Error("Vault is locked");
    this._touch();
    const enc = await encryptObject(key, obj);
    await vaultPut(id, enc, ns);
  }

  /* -------- Backup v2 --------------------------------------------------- */
  async exportBackup() {
    if (!this.isUnlocked()) throw new Error("Unlock the vault before exporting");
    const namespaces = {};
    for (const ns of NS_LIST) {
      const salt = await metaGet(META_SALT, ns);
      const sent = await metaGet(META_SENTINEL, ns);
      const recs = await vaultDump(ns);
      namespaces[ns] = { salt: _internals._u8ToB64(salt), sentinel: sent, records: recs };
    }
    return {
      format: "tp-private-vault-backup",
      version: 2,
      exportedAt: new Date().toISOString(),
      namespaces,
    };
  }

  async importBackup(backup, pin) {
    if (!backup || backup.format !== "tp-private-vault-backup")
      throw new Error("Not a valid vault backup file");

    if (backup.version === 2) {
      // Verify PIN unlocks every namespace sentinel before writing anything
      const unlocked = {};
      for (const ns of NS_LIST) {
        const nb = (backup.namespaces || {})[ns];
        if (!nb) throw new Error(`Backup is missing the ${ns} namespace`);
        const salt = _internals._b64ToU8(nb.salt);
        const key  = await deriveKey(pin, salt);
        try { await decryptObject(key, nb.sentinel); }
        catch { throw new Error("Incorrect PIN for this backup file"); }
        unlocked[ns] = { salt, key, nb, bytes: await this._deriveKeyBytes(pin, salt) };
      }
      let imported = 0;
      for (const ns of NS_LIST) {
        const { salt, nb, key, bytes } = unlocked[ns];
        await metaSet(META_SALT, salt, ns);
        await metaSet(META_SENTINEL, nb.sentinel, ns);
        await metaSet(META_VERSION, CURRENT_VERSION, ns);
        for (const [id, enc] of Object.entries(nb.records || {})) {
          await vaultPut(id, enc, ns);
          imported++;
        }
        this._keys[ns] = key;
        this._keyBytes[ns] = bytes;
      }
      await bio.removeBiometric();
      this._touch();
      this._emit();
      return { imported };
    }

    // v1 legacy backup: single flat record map keyed by compartment id
    if (backup.version !== 1) throw new Error(`Unsupported backup version: ${backup.version}`);
    const legacySalt = _internals._b64ToU8(backup.salt);
    const legacyKey  = await deriveKey(pin, legacySalt);
    try { await decryptObject(legacyKey, backup.sentinel); }
    catch { throw new Error("Incorrect PIN for this backup file"); }

    for (const ns of NS_LIST) await this._seedNamespace(ns, pin);
    let imported = 0;
    for (const [cid, enc] of Object.entries(backup.records || {})) {
      const ns = nsFor(cid);
      if (ns === VAULT_NS.DEFAULT) continue;
      try {
        const obj = await decryptObject(legacyKey, enc);
        const re  = await encryptObject(this._keys[ns], obj);
        await vaultPut(cid, re, ns);
        imported++;
      } catch { /* best effort */ }
    }
    await bio.removeBiometric();
    this._touch();
    this._emit();
    return { imported };
  }

  async wipe() {
    this.lock();
    await bio.removeBiometric();
    // Clear per-ns metadata keys for both namespaces + legacy DEFAULT
    for (const ns of [...NS_LIST, VAULT_NS.DEFAULT]) {
      await metaDelete(META_SALT, ns);
      await metaDelete(META_SENTINEL, ns);
      await metaDelete(META_VERSION, ns);
      await metaDelete(META_HINT, ns);
    }
    await wipeEverything();   // nukes DEFAULT + HEALTH + ITEMS databases outright
    this._emit();
  }
}

const vaultService = new VaultService();

// Lock the vault if the tab is hidden for a minute+
if (typeof document !== "undefined") {
  let hiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt > 60 * 1000 && vaultService.isUnlocked()) {
      vaultService.lock();
    }
  });
}

export default vaultService;
export { COMPARTMENT };
